/**
 * Core hook for plugin builder session management.
 * Handles session CRUD, streaming, guided flow state, undo/redo, verbose events, and screenshots.
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useStreaming } from '@/lib/hooks/useStreaming';
import { authApi } from '@/lib/auth-api';
import api, { ApiError } from '@/lib/api';
import { addBreadcrumb } from '@/lib/telemetry';
import { RETRYABLE_FAILURE_CODES } from '../lib/builder-failure-codes';
import {
  PREVIEW_BUILD_FAILURE_ELAPSED_CAP_MS,
  PREVIEW_BUILD_FAILURE_MESSAGE_MAX,
} from '../lib/preview-build-report-bounds';
import { useUndoRedo } from './useUndoRedo';
import {
  loadHistory,
  saveHistory,
  cleanupOldEntries,
  UNKNOWN_BASE_VERSION,
} from '../lib/history-store';
import type {
  PluginBuilderSession,
  VirtualFileSystem,
  GuidedPhase,
  GuidedFlow,
  BuildPlan,
  BuilderMode,
  VerboseEvent,
} from '../types';
import { WEBSOCKET_ENDPOINT } from '@/lib/api';
import type { PluginPermission } from '@fias/arche-sdk';

/**
 * The API error code of a failed request (e.g. `INSUFFICIENT_CREDITS` from a
 * turn's 402 pre-check), forwarded to `onError` so the page can show the
 * actionable prompt — the buy-credits CTA, or sign-up for a guest whose trial
 * is spent — instead of a generic error.
 */
function apiErrorCode(err: unknown): string | undefined {
  return err instanceof ApiError ? err.code : undefined;
}

/**
 * Tell the server about a preview build request that was refused WITHOUT
 * reaching the bundler, so the AI's preview tools report "the request was
 * refused — the code was not compiled" rather than reading a code problem into
 * stale log lines. A bundler error (`BUILD_ERROR`, 422) is recorded by the
 * build handler itself and is skipped here. The status and API code travel
 * with it so the server words the status by cause (size limit vs rate limit vs
 * platform failure); the server stamps the ordering time on receipt. Only the
 * failure of the BUILD REQUEST itself is reported — never an exception from
 * the host's own post-build handling — and only for a build the user can see
 * fail (a silent background sync keeps the mounted preview, so there is
 * nothing for the AI to explain). Fire-and-forget: the pane shows the error.
 */
function reportPreviewBuildRefusal(sessionId: string, err: unknown, issuedAtMono: number): void {
  const code = apiErrorCode(err);
  if (code === 'BUILD_ERROR') return;
  const message =
    (err instanceof Error ? err.message : String(err)) || 'Preview build request failed';
  const httpStatus = err instanceof ApiError ? err.status : 0;
  // Monotonic, so a wrong wall clock cannot skew it; clamped to the server's
  // backdate cap (its schema max) so a build that slept past it (laptop lid)
  // still gets its report through.
  const elapsedMs = Math.min(
    PREVIEW_BUILD_FAILURE_ELAPSED_CAP_MS,
    Math.max(0, Math.round(performance.now() - issuedAtMono)),
  );
  void api
    .reportPluginPreviewBuildFailure(sessionId, {
      message: message.slice(0, PREVIEW_BUILD_FAILURE_MESSAGE_MAX),
      httpStatus,
      ...(code ? { errorCode: code } : {}),
      elapsedMs,
    })
    .catch((reportErr: unknown) => {
      // Best-effort — the pane shows the error regardless — but a dropped
      // report should be visible in telemetry, not invisible.
      addBreadcrumb('builder.preview', 'preview build failure report dropped', {
        sessionId,
        code: code ?? null,
        httpStatus,
        reportError: reportErr instanceof Error ? reportErr.message : String(reportErr),
      });
    });
}

/**
 * Stall watchdog. A builder turn streams frames (tokens, files_update,
 * settings_update) every few seconds while it's alive. If the backend dies
 * mid-turn — e.g. the API task is OOM-killed after the AI stream completes but
 * before the terminal DB write — no `complete` or `error` frame is ever sent,
 * so `isSending`/`isGuidedLoading` would stay true forever: the spinner hangs
 * and, because a live stream hard-blocks `refreshIfStale`, the tab can never
 * self-heal. We treat a sustained silence as a dead turn. Detection is a cheap
 * poll of the last-activity timestamp rather than a per-frame timer reset.
 *
 * Threshold is deliberately generous: healthy turns emit frames every 1–3s
 * (verified in prod logs), and the longest legitimate gap is a server-side
 * esbuild verify between iterations — still far under two minutes.
 */
const STREAM_STALL_TIMEOUT_MS = 120_000;
const STREAM_STALL_CHECK_INTERVAL_MS = 20_000;

/**
 * Structural equality for two virtual file systems. Used to decide whether a
 * background server sync actually changed the code (→ rebuild the preview) or
 * only bumped the session version via a settings/metadata write (→ skip the
 * rebuild so the running preview, and any in-progress report, survives). Errs
 * toward "changed" on any shape mismatch — a redundant rebuild is harmless,
 * a missed real change is not.
 */
function vfsEqual(a: VirtualFileSystem | null, b: VirtualFileSystem | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  if (aKeys.length !== bKeys.length) return false;
  for (let i = 0; i < aKeys.length; i++) {
    if (aKeys[i] !== bKeys[i]) return false;
  }
  for (const key of aKeys) {
    if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) return false;
  }
  return true;
}

interface UsePluginBuilderSessionOptions {
  verbose?: boolean;
  useOpus?: boolean;
  onVerboseEvent?: (event: VerboseEvent) => void;
  onError?: (err: Error, code?: string) => void;
  /**
   * Called when the hook pulls a newer session state made on another device
   * (tab refocus or a save conflict). The page uses it to toast the user so a
   * silent mid-edit content swap isn't a surprise. `reason` is 'focus' |
   * 'conflict'.
   */
  onSyncFromRemote?: (reason: string) => void;
  /**
   * Called when the stall watchdog trips — the in-flight turn went silent long
   * enough to be considered dead (see STREAM_STALL_TIMEOUT_MS). Fires BEFORE the
   * hook flips `isSending`/`isGuidedLoading` back to false, so the page can
   * stand down its auto-approve continuation (set userStopped, disable
   * auto-approve, clear the pending-continue timer) and not silently re-run —
   * and re-bill — the dead turn. The hook itself surfaces the retryable error
   * and reconciles session state; this callback is only the auto-approve
   * coupling the hook can't reach.
   */
  onStall?: () => void;
  /**
   * Invoked when a completed turn carried `creditStop` — the auto-mode build
   * stopped at a step boundary because the user can't afford the next step.
   * The page disarms auto-approve and shows the buy-credits CTA.
   */
  onCreditStop?: () => void;
  /** Invoked (each completion that carries it) when the session is long enough
   *  that older context is summarized — the page shows a one-time notice. */
  onContextPressure?: () => void;
  /**
   * Invoked when a session load healed a wedged build plan by repointing the
   * pointer BACKWARD (Phase 4). The page explains the step-number drop
   * ("resumed at the earliest unfinished step") instead of staying silent.
   */
  onBuildPlanRepointed?: (info: { toStepTitle?: string }) => void;
  /** Silence (ms) before the stall watchdog trips. Override for tests. */
  stallTimeoutMs?: number;
  /** Poll interval (ms) for the stall watchdog. Override for tests. */
  stallCheckIntervalMs?: number;
  /**
   * Returns the host's current preview light/dark mode. Threaded into every
   * preview build so the server bakes the matching theme into the initial
   * paint — without it, a rebuild flashes the wrong-mode background (dark in
   * light mode). A getter (not a value) so the []‑dep `buildPreview` always
   * reads the current mode, including auto-builds fired after AI steps.
   */
  getPreviewMode?: () => 'light' | 'dark';
  /**
   * Called immediately before an in-place preview rebuild swaps the iframe's
   * `srcDoc` (which reloads the iframe and destroys the running plugin's
   * in-memory state — e.g. a generated report). Lets the host snapshot the
   * live plugin via the bridge so it can be restored after the reload. Awaited
   * so the snapshot round-trip completes before the old iframe is torn down.
   * Only fired for a genuine reload (when a preview is already rendered), not
   * the first build.
   */
  onBeforePreviewReload?: () => Promise<void> | void;
  /**
   * When true at swap time, a non-forced rebuild does NOT replace the mounted
   * preview — the fresh HTML is stashed and `hasPendingPreview` turns on so
   * the pane can show a "Preview updated — Refresh" pill (Phase 5: never
   * yank the app out from under a user who is actively testing it).
   */
  shouldDeferPreviewSwap?: () => boolean;
}

export interface UsePluginBuilderSessionReturn {
  // Session state
  session: PluginBuilderSession | null;
  isLoading: boolean;
  error: string | null;

  // Files
  files: VirtualFileSystem | null;

  // Conversation
  messages: Array<{ role: 'user' | 'assistant'; content: string; timestamp?: string }>;
  streamingText: string;
  /**
   * Plain-English label for what the assistant is doing right now
   * ("Writing code…"), or null when idle / actively streaming text.
   */
  currentActivity: string | null;
  isSending: boolean;
  /** Typed failure of the last turn; retryable drives the inline Retry. */
  lastFailure: { code: string; message: string; retryable: boolean } | null;
  /** Re-send the last user message (Retry for transient typed failures). */
  retryLastMessage: () => void;
  /** A fresher preview build is stashed (deferred swap) — show the pill. */
  hasPendingPreview: boolean;
  /** Apply the stashed preview (captures state first via onBeforePreviewReload). */
  applyPendingPreview: () => Promise<void>;
  /** True when the last AI turn was cut off at the output cap and not recovered. */
  truncated: boolean;

  // Guided flow
  guidedFlow: GuidedFlow | null;
  buildPlan: BuildPlan | null;
  builderMode: BuilderMode;
  isGuidedLoading: boolean;
  guidedError: string | null;

  // Undo/redo
  canUndo: boolean;
  canRedo: boolean;
  undoCount: number;
  redoCount: number;
  undo: () => void;
  redo: () => void;

  // Actions
  loadSession: (sessionId: string) => Promise<void>;
  sendMessage: (message: string, guidedPhase?: GuidedPhase, autoApprove?: boolean) => Promise<void>;
  startGuidedFlow: (idea: string) => Promise<void>;
  setBuilderMode: (mode: BuilderMode) => void;
  saveSession: (name: string, description?: string) => Promise<void>;
  resetContext: () => Promise<void>;
  stopGeneration: () => void;
  uploadPreviewScreenshot: (dataUrl: string) => void;
  advanceStep: () => Promise<boolean>;

  // Programmatic guided flow actions
  submitAnswers: (answers: Record<number, string>, comment?: string) => Promise<void>;
  approvePlan: () => Promise<void>;
  requestPlanChanges: (feedback: string) => Promise<void>;
  approveAssumptions: () => Promise<void>;
  suggestAssumptionChanges: (comments: Record<string, string>) => Promise<void>;
  applyPendingGuidedResult: () => void;
  hasPendingGuidedResult: boolean;
  cancelGuidedRequest: () => void;
  /**
   * Rewinds the guided flow. Rejects with the API error (code
   * `REWIND_LOSES_PROGRESS`, 409) when the rewind would delete the plan or
   * reset progress and `confirmProgressLoss` was not passed — the page turns
   * that into the confirmation dialog.
   */
  rewindToPhase: (
    targetPhase: GuidedPhase,
    options?: { confirmProgressLoss?: boolean; expectedVersion?: number },
  ) => Promise<void>;

  // Preview
  buildPreview: (
    files?: VirtualFileSystem,
    opts?: { silent?: boolean; force?: boolean },
  ) => Promise<string | null>;
  previewHtml: string | null;
  /**
   * Permissions the mounted preview enforces, resolved server-side and shipped
   * with the build. `null` before the first build (or from a server predating
   * the field) — consumers fall back to the manifest's declared array.
   */
  previewPermissions: PluginPermission[] | null;
  isBuildingPreview: boolean;
  buildError: string | null;

  // State indicators
  isResetting: boolean;

  // SDK update
  sdkUpdate: { available: boolean; from: string | null; to: string; hasBreaking: boolean } | null;
  isUpgradingSdk: boolean;
  upgradeSdk: () => Promise<void>;

  /** Optimistically set the session name (header updates immediately) */
  setSessionName: (name: string) => void;

  /** Optimistically set the session short description so the publish dialog reads the latest value */
  setSessionDescription: (description: string) => void;

  /** Optimistically set the session expanded description (About) */
  setSessionExpandedDescription: (expandedDescription: string) => void;

  /** Optimistically update session settings (so config panel reflects changes without reload) */
  updateSessionSettings: (settings: Record<string, unknown>) => void;

  /** Optimistically update the build plan state (e.g. toggling node showUI) */
  setBuildPlan: (plan: BuildPlan | null) => void;

  /**
   * Record the server `version` returned by a tab-initiated session PATCH
   * (settings, name, description). Tab-initiated writes bump the server version
   * but, being fire-and-forget, leave the hook's optimistic-locking baseline
   * behind — which makes the focus-refresh think the server is ahead and rebuild
   * the preview on every refocus, destroying any in-progress report. Threading
   * the returned version back here keeps the baseline in sync.
   */
  noteServerVersion: (version: number | undefined) => void;
}

export function usePluginBuilderSession(
  options: UsePluginBuilderSessionOptions = {},
): UsePluginBuilderSessionReturn {
  const {
    verbose = false,
    useOpus = false,
    onVerboseEvent,
    onError,
    onSyncFromRemote,
    onStall,
    onCreditStop,
    onContextPressure,
    onBuildPlanRepointed,
    stallTimeoutMs = STREAM_STALL_TIMEOUT_MS,
    stallCheckIntervalMs = STREAM_STALL_CHECK_INTERVAL_MS,
    getPreviewMode,
    onBeforePreviewReload,
    shouldDeferPreviewSwap,
  } = options;

  const authToken = authApi.getIdToken();
  const [session, setSession] = useState<PluginBuilderSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<VirtualFileSystem | null>(null);
  const filesRef = useRef<VirtualFileSystem | null>(null);
  const [messages, setMessages] = useState<
    Array<{ role: 'user' | 'assistant'; content: string; timestamp?: string }>
  >([]);
  const [isSending, setIsSending] = useState(false);
  // True when the last AI turn was cut off at the output cap (and the server's
  // single auto-continuation didn't recover it). Drives the "response was cut
  // off — Continue" affordance in the conversation pane.
  const [truncated, setTruncated] = useState(false);
  // Typed failure of the last turn (stability overhaul 1a/1b): drives the
  // inline Retry affordance and the step panel's failure card. Cleared when a
  // new turn starts or one completes. `retryable` derives from the code — the
  // typed codes ARE the client contract (no extra frame plumbing needed).
  const [lastFailure, setLastFailure] = useState<{
    code: string;
    message: string;
    retryable: boolean;
  } | null>(null);
  // True between an error frame and its trailing complete frame — see
  // onError/onComplete. Reset by the complete that consumes it.
  const errorTerminalStreamRef = useRef(false);
  // Args of the most recent sendMessage, for one-click Retry after a
  // transient failure. Ref (not state): never triggers renders.
  const lastSentRef = useRef<{
    message: string;
    guidedPhase?: GuidedPhase;
    autoApprove?: boolean;
  } | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [sdkUpdate, setSdkUpdate] = useState<{
    available: boolean;
    from: string | null;
    to: string;
    hasBreaking: boolean;
  } | null>(null);
  const [isUpgradingSdk, setIsUpgradingSdk] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [guidedFlow, setGuidedFlow] = useState<GuidedFlow | null>(null);
  const [buildPlan, setBuildPlan] = useState<BuildPlan | null>(null);
  const [builderMode, setBuilderMode] = useState<BuilderMode>('guided');
  const [isGuidedLoading, setIsGuidedLoading] = useState(false);
  const [guidedError, setGuidedError] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  // Synchronous mirror of previewHtml so buildPreview's idempotency guard can
  // compare the freshly-built HTML against what's already mounted without
  // waiting for a state read.
  const previewHtmlRef = useRef<string | null>(null);
  /**
   * THE permission set the mounted preview enforces, resolved SERVER-side and
   * delivered with the build it belongs to (`baseline ∪ derived-from-source ∪
   * valid-declared ∪ settings-provisioned` — the same function the publish path
   * runs). `null` until the first build response, or when an older server
   * omitted the field; the page then falls back to the manifest's declared
   * array.
   *
   * Kept in lockstep with `previewHtml` at every writer, including the
   * identical-HTML and deferred-swap branches. A manifest-only edit produces
   * byte-identical HTML, so the iframe never reloads and the bridge host
   * survives — if the permissions did not move with it, the host would keep
   * enforcing a stale set, which is the "Permission denied after the manifest
   * was already fixed" shape PluginBridge documents.
   */
  const [previewPermissions, setPreviewPermissions] = useState<PluginPermission[] | null>(null);
  const [isBuildingPreview, setIsBuildingPreview] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);

  // Track active guided request for cancellation
  const guidedAbortRef = useRef<AbortController | null>(null);

  // Track guided stream request ID — when set, onComplete knows to handle as guided flow
  const guidedStreamRequestIdRef = useRef<string | null>(null);

  // Deferred guided result — stores the API result so the page can show
  // the AI message first, then apply the structured result when Continue is clicked
  const pendingGuidedResultRef = useRef<{
    guidedFlow?: GuidedFlow;
    buildPlan?: BuildPlan | null;
  } | null>(null);
  const [hasPendingGuidedResult, setHasPendingGuidedResult] = useState(false);

  // Track accumulated streaming text per node
  const streamingTextRef = useRef<string>('');

  // Plain-English "what's happening" line ("Writing code…") shown while the
  // model works. Set by `activity` events; a label PERSISTS until the next
  // label replaces it or the turn ends. Deliberately NOT cleared when text
  // streams: the model is still inside the same tool phase while it narrates,
  // and clearing on the first token reduced the label to an unreadable flash
  // (the guided message-first ordering means tokens arrive <1s after the
  // tool call starts).
  const [currentActivity, setCurrentActivity] = useState<string | null>(null);

  const showActivity = useCallback((label: string) => {
    setCurrentActivity(label);
  }, []);

  const clearActivity = useCallback(() => {
    setCurrentActivity(null);
  }, []);

  // Refs to avoid stale closures in callbacks
  const verboseRef = useRef(verbose);
  verboseRef.current = verbose;
  const useOpusRef = useRef(useOpus);
  useOpusRef.current = useOpus;
  const onVerboseEventRef = useRef(onVerboseEvent);
  onVerboseEventRef.current = onVerboseEvent;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onSyncFromRemoteRef = useRef(onSyncFromRemote);
  onSyncFromRemoteRef.current = onSyncFromRemote;
  const onStallRef = useRef(onStall);
  onStallRef.current = onStall;
  const onCreditStopRef = useRef(onCreditStop);
  onCreditStopRef.current = onCreditStop;
  const onContextPressureRef = useRef(onContextPressure);
  onContextPressureRef.current = onContextPressure;
  const onBuildPlanRepointedRef = useRef(onBuildPlanRepointed);
  onBuildPlanRepointedRef.current = onBuildPlanRepointed;
  // Dedupe: the focus-refetch would otherwise re-announce the same heal.
  const lastHealKeyRef = useRef<string | null>(null);

  const announceHealIfRepointed = useCallback(
    (healed?: { reasons: string[]; repointed: boolean; toStepTitle?: string }) => {
      if (!healed?.repointed) return;
      const key = healed.reasons.join('|');
      if (lastHealKeyRef.current === key) return;
      lastHealKeyRef.current = key;
      onBuildPlanRepointedRef.current?.({
        ...(healed.toStepTitle ? { toStepTitle: healed.toStepTitle } : {}),
      });
    },
    [],
  );
  // Turn-start baseline for the stall watchdog. Actual per-frame liveness comes
  // from the transport (streaming.getLastMessageAt(), which counts EVERY inbound
  // frame including the server heartbeat); the watchdog takes the max of the two
  // so a turn that has produced no frames yet is measured from its start.
  const turnStartRef = useRef<number>(0);
  const getPreviewModeRef = useRef(getPreviewMode);
  getPreviewModeRef.current = getPreviewMode;
  const onBeforePreviewReloadRef = useRef(onBeforePreviewReload);
  onBeforePreviewReloadRef.current = onBeforePreviewReload;
  const shouldDeferPreviewSwapRef = useRef(shouldDeferPreviewSwap);
  shouldDeferPreviewSwapRef.current = shouldDeferPreviewSwap;
  // Deferred-swap stash (Phase 5): the freshest built-but-not-mounted HTML.
  const pendingPreviewHtmlRef = useRef<{
    html: string;
    permissions: PluginPermission[] | null;
  } | null>(null);
  const [hasPendingPreview, setHasPendingPreview] = useState(false);
  const sessionIdRef = useRef<string | null>(null);
  // Latest known server session.version — the cross-device freshness token.
  // Updated from every server response (load, stream-complete, file PATCH).
  // `UNKNOWN_BASE_VERSION` until the first load resolves.
  const serverVersionRef = useRef<number>(UNKNOWN_BASE_VERSION);
  // Advance the freshness baseline from a tab-initiated PATCH's returned version
  // so fire-and-forget settings/name/description writes don't leave the baseline
  // behind and trip refreshIfStale into a spurious preview rebuild. Monotonic:
  // never moves backward.
  const noteServerVersion = useCallback((version: number | undefined) => {
    if (typeof version === 'number' && version > serverVersionRef.current) {
      serverVersionRef.current = version;
    }
  }, []);
  // Forward ref for refreshIfStale — defined after loadSession but referenced
  // by the earlier scheduleBackendSave 409 handler and the focus listener.
  const refreshIfStaleRef = useRef<(reason: string) => void>(() => {});
  // Forward ref for buildPreview — assigned after declaration, used in early callbacks
  const buildPreviewRef = useRef<
    (
      files?: VirtualFileSystem,
      opts?: { silent?: boolean; force?: boolean },
    ) => Promise<string | null>
  >(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    null as any,
  );
  // Forward ref for streaming.reset — the useStreaming hook is declared below, so
  // callbacks passed into it close over this ref and call through it at invocation time
  const streamingResetRef = useRef<() => void>(() => {});
  const guidedFlowRef = useRef(guidedFlow);
  guidedFlowRef.current = guidedFlow;
  // Synced ref so the focus-refresh guard can read "is a stream in flight"
  // without re-subscribing the window listener every render.
  const isSendingRef = useRef(isSending);
  isSendingRef.current = isSending;

  // Accumulates the latest files during streaming; snapshot is flushed once in onComplete
  const pendingSnapshotFilesRef = useRef<VirtualFileSystem | null>(null);

  // ─── Undo/Redo ──────────────────────────────────────────────────────────────

  const {
    canUndo,
    canRedo,
    undoCount,
    redoCount,
    snapshot: takeSnapshot,
    undo: undoHistory,
    redo: redoHistory,
    getHistory,
    restore: restoreHistory,
  } = useUndoRedo<VirtualFileSystem>({ maxHistory: 50 });

  const takeSnapshotRef = useRef(takeSnapshot);
  takeSnapshotRef.current = takeSnapshot;
  const getHistoryRef = useRef(getHistory);
  getHistoryRef.current = getHistory;

  // Debounced save timer for IndexedDB persistence
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced backend persist timer for undo/redo file syncing
  const backendSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleBackendSave = useCallback((filesToSave: VirtualFileSystem) => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    if (backendSaveTimerRef.current) clearTimeout(backendSaveTimerRef.current);
    backendSaveTimerRef.current = setTimeout(() => {
      // Declare the base version we're editing from so the server can reject a
      // save that would clobber a newer edit made on another device. Omit it
      // until we've loaded once (no baseline to assert yet).
      const expectedVersion = serverVersionRef.current >= 0 ? serverVersionRef.current : undefined;
      api
        .updatePluginBuilderSessionFiles(
          sid,
          filesToSave as Record<string, unknown>,
          expectedVersion,
        )
        .then((res) => {
          // Advance our freshness baseline to the server's new version and
          // re-persist the undo history tagged with it, so the snapshot
          // survives a refresh on this device (baseVersion stays in sync).
          if (res && typeof res.version === 'number') {
            serverVersionRef.current = res.version;
            scheduleSaveRef.current();
          }
        })
        .catch((err) => {
          // 409 = another device advanced the row past our base version.
          // Do NOT blindly re-send — that would overwrite the newer edit.
          // Reconcile by pulling the latest server state (server wins) and
          // notifying the user.
          if (err instanceof Error && err.message.includes('409')) {
            refreshIfStaleRef.current('conflict');
            return;
          }
          console.warn('[usePluginBuilderSession] Failed to persist undo/redo files:', err);
        });
    }, 500);
  }, []);

  const scheduleSave = useCallback(() => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const { states, index } = getHistoryRef.current();
      if (states.length > 0) {
        saveHistory(sid, states, index, serverVersionRef.current);
      }
    }, 1_000);
  }, []);

  const scheduleSaveRef = useRef(scheduleSave);
  scheduleSaveRef.current = scheduleSave;

  // Load undo history from IndexedDB on session load. Returns the raw record so
  // the caller can decide whether it's still current (same server version) and
  // should be restored, or stale (server edited elsewhere) and should be
  // discarded. Restoring is intentionally NOT done here — that decision belongs
  // to the version-reconcile in loadSession.
  const loadUndoHistory = useCallback(async (sessionId: string) => {
    const saved = await loadHistory(sessionId);
    // Cleanup old entries
    cleanupOldEntries();
    return saved; // { states, index, baseVersion } | null
  }, []);

  // Flush pending saves on unmount
  useEffect(() => {
    return () => {
      // Flush any pending snapshot from an in-flight stream
      if (pendingSnapshotFilesRef.current) {
        takeSnapshotRef.current(pendingSnapshotFilesRef.current);
        pendingSnapshotFilesRef.current = null;
      }
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        const sid = sessionIdRef.current;
        if (sid) {
          const { states, index } = getHistoryRef.current();
          if (states.length > 0) {
            saveHistory(sid, states, index, serverVersionRef.current);
          }
        }
      }
      if (backendSaveTimerRef.current) {
        clearTimeout(backendSaveTimerRef.current);
      }
    };
  }, []);

  // ─── Streaming ──────────────────────────────────────────────────────────────

  const streaming = useStreaming({
    requestId,
    authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    onTextUpdate: useCallback((_nodeId: string, text: string) => {
      streamingTextRef.current += text;
    }, []),
    onActivity: showActivity,
    onSettingsUpdate: useCallback((settings: Record<string, unknown>) => {
      // During guided streaming, don't apply guidedFlow/buildPlan from settings_update events
      // — the structured result comes via the complete event payload instead
      if (guidedStreamRequestIdRef.current) return;
      if (settings.guidedFlow) {
        setGuidedFlow(settings.guidedFlow as GuidedFlow);
      }
      if (settings.buildPlan) {
        setBuildPlan(settings.buildPlan as BuildPlan);
      }
    }, []),
    onFilesUpdate: useCallback((updatedFiles: Record<string, unknown>) => {
      const vfs = updatedFiles as VirtualFileSystem;
      setFiles(vfs);
      filesRef.current = vfs;
      // Accumulate latest files — snapshot is taken once when the stream completes
      // (not per tool call) so undo reverts the entire AI response, not individual edits
      pendingSnapshotFilesRef.current = vfs;
    }, []),
    onComplete: useCallback(
      (completedRequestId: string, payload?: Record<string, unknown>) => {
        clearActivity();
        const isGuidedStream = guidedStreamRequestIdRef.current === completedRequestId;

        // Surface a cut-off turn (set by the server when its auto-continuation
        // couldn't finish the change). Applies to either stream type.
        setTruncated(payload?.truncated === true);
        // A SUCCESSFUL completion supersedes any prior typed failure — but the
        // complete frame that trails an error frame (sent to unlock the
        // client) must NOT: it arrives one tick after onError set the state,
        // and clearing here made the Retry affordance unusable. sendMessage
        // clears the failure when a new turn actually starts.
        if (errorTerminalStreamRef.current) {
          errorTerminalStreamRef.current = false;
        } else {
          setLastFailure(null);
        }
        // Auto-mode credit boundary: stop the auto-continue loop and surface
        // the top-up CTA BEFORE another doomed turn fires (2b).
        if (payload?.creditStop === true) {
          onCreditStopRef.current?.();
        }
        if (payload?.contextPressure === 'high') {
          onContextPressureRef.current?.();
        }

        if (isGuidedStream) {
          // Guided stream completed — apply the result from the payload
          guidedStreamRequestIdRef.current = null;
          setIsGuidedLoading(false);

          // Add assistant message from accumulated streaming text
          const assistantText = streamingTextRef.current.trim();
          if (assistantText) {
            setMessages((prev) => [
              ...prev,
              { role: 'assistant', content: assistantText, timestamp: new Date().toISOString() },
            ]);
          }
          streamingTextRef.current = '';
          setRequestId(null);

          // Stash the structured result from the complete event payload
          const guidedResult = payload?.guidedResult as Record<string, unknown> | undefined;
          if (guidedResult) {
            pendingGuidedResultRef.current = {
              guidedFlow: guidedResult.guidedFlow as GuidedFlow,
              buildPlan: (guidedResult.buildPlan as BuildPlan) ?? null,
            };
            setHasPendingGuidedResult(true);
          }

          // Flush pending snapshot — one snapshot per AI response (not per tool call)
          if (pendingSnapshotFilesRef.current) {
            takeSnapshotRef.current(pendingSnapshotFilesRef.current);
            scheduleSaveRef.current();
            pendingSnapshotFilesRef.current = null;
          }

          // Clear the streaming-text state inside useStreaming so the next turn
          // doesn't briefly render this response's text while isProcessing is true
          streamingResetRef.current();

          // Build preview with current files after guided stream completes
          const guidedFilesToBuild = filesRef.current;
          if (guidedFilesToBuild && Object.keys(guidedFilesToBuild).length > 0) {
            buildPreviewRef.current(guidedFilesToBuild);
          }

          // Re-fetch session to update credit usage (totalCostCents).
          // Only update the session object — guidedFlow/buildPlan come from the payload above.
          const guidedSid = sessionIdRef.current;
          if (guidedSid) {
            api.getPluginBuilderSession(guidedSid).then((result) => {
              const sess = result.session as PluginBuilderSession;
              setSession(sess);
              // The stream wrote files server-side and bumped the version.
              // Advance our baseline and re-persist the just-taken snapshot
              // tagged with the new version so undo survives a refresh.
              if (typeof sess.version === 'number') {
                serverVersionRef.current = sess.version;
                scheduleSaveRef.current();
              }
            });
          }
          return;
        }

        // Regular chat stream completed
        setIsSending(false);
        setRequestId(null);

        // Add assistant message from accumulated text
        const assistantText = streamingTextRef.current.trim();
        if (assistantText) {
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: assistantText, timestamp: new Date().toISOString() },
          ]);
        }
        streamingTextRef.current = '';

        // Flush pending snapshot — one snapshot per AI response (not per tool call)
        if (pendingSnapshotFilesRef.current) {
          takeSnapshotRef.current(pendingSnapshotFilesRef.current);
          scheduleSaveRef.current();
          pendingSnapshotFilesRef.current = null;
        }

        // Clear the streaming-text state inside useStreaming so the next turn
        // doesn't briefly render this response's text while isProcessing is true
        streamingResetRef.current();

        // Build preview with current files after stream completes
        const filesToBuild = filesRef.current;
        if (filesToBuild && Object.keys(filesToBuild).length > 0) {
          buildPreviewRef.current(filesToBuild);
        }

        // Re-fetch session to get latest state (credit usage, guided flow, build plan).
        // DO NOT setFiles here — files are already current from onFilesUpdate events.
        // Re-fetching would overwrite any undo the user performed while the stream was completing.
        const sid = sessionIdRef.current;
        if (sid) {
          api.getPluginBuilderSession(sid).then((result) => {
            const sess = result.session as PluginBuilderSession;
            setSession(sess);
            // The stream wrote files server-side and bumped the version.
            // Advance our baseline and re-persist the just-taken snapshot
            // tagged with the new version so undo survives a refresh.
            if (typeof sess.version === 'number') {
              serverVersionRef.current = sess.version;
              scheduleSaveRef.current();
            }
            const settings = sess.currentSettings as Record<string, unknown>;
            if (settings?.guidedFlow) {
              setGuidedFlow(settings.guidedFlow as GuidedFlow);
            }
            if (settings?.buildPlan) {
              setBuildPlan(settings.buildPlan as BuildPlan);
            }
          });
        }
      },
      [], // Using ref instead of session?.sessionId dependency
    ),
    onError: useCallback((code: string, message: string) => {
      clearActivity();
      // The server follows every error frame with a `complete` frame (it
      // unlocks the client). Mark this stream error-terminal so that trailing
      // complete doesn't wipe the failure state one tick after it was set
      // (review finding: the inline Retry flashed and vanished).
      errorTerminalStreamRef.current = true;
      setLastFailure({ code, message, retryable: RETRYABLE_FAILURE_CODES.has(code) });
      // If this error came from a guided stream, surface it as a guided error
      // and clear guided loading state so the user isn't left waiting
      if (guidedStreamRequestIdRef.current) {
        guidedStreamRequestIdRef.current = null;
        setIsGuidedLoading(false);
        setGuidedError(message);
      }
      setError(message);
      setIsSending(false);
      // A fresh error supersedes any prior cut-off notice — clear it so a stale
      // "Continue" affordance doesn't linger (covers guided-action error paths
      // that never reach onComplete).
      setTruncated(false);
      // Forward the error code (e.g. INSUFFICIENT_CREDITS) so the page can show
      // an actionable prompt — a credit shortfall mid-stream is not an "internal
      // error", it just means the user needs to top up.
      onErrorRef.current?.(new Error(message), code);
      // Flush pending snapshot so partial changes from a failed stream are still undoable
      if (pendingSnapshotFilesRef.current) {
        takeSnapshotRef.current(pendingSnapshotFilesRef.current);
        scheduleSaveRef.current();
        pendingSnapshotFilesRef.current = null;
      }
      // Clear streaming-text state so a retry doesn't flash the failed turn's partial text
      streamingResetRef.current();
      // Build preview with whatever partial files exist
      const errorFiles = filesRef.current;
      if (errorFiles && Object.keys(errorFiles).length > 0) {
        buildPreviewRef.current(errorFiles);
      }
    }, []),
    // Verbose event callbacks
    onSystemPrompt: useCallback((prompt: string) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'system_prompt',
          timestamp: new Date().toISOString(),
          data: { prompt },
        });
      }
    }, []),
    onToolCall: useCallback((toolName: string, toolInput: unknown) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'tool_call',
          timestamp: new Date().toISOString(),
          data: { toolName, toolInput },
        });
      }
    }, []),
    onToolResult: useCallback((toolName: string, toolResult: unknown, isError: boolean) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'tool_result',
          timestamp: new Date().toISOString(),
          data: { toolName, toolResult, isError },
        });
      }
    }, []),
    onModelInfo: useCallback((model: string, intent: string) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'model_info',
          timestamp: new Date().toISOString(),
          data: { model, intent },
        });
      }
    }, []),
    onToolsAvailable: useCallback((tools: Array<{ name: string; description: string }>) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'tools_available',
          timestamp: new Date().toISOString(),
          data: { tools },
        });
      }
    }, []),
  });

  // Current streaming text (combined from all nodes)
  const currentStreamingText = useMemo(
    () => Object.values(streaming.streamingText).join(''),
    [streaming.streamingText],
  );

  // ─── Session Actions ────────────────────────────────────────────────────────

  const loadSession = useCallback(
    async (sessionId: string) => {
      setIsLoading(true);
      setError(null);
      sessionIdRef.current = sessionId;
      // Treat the first build of a freshly-loaded session as a first build, not
      // a reload — so we don't snapshot the previous session's plugin and try to
      // restore it into a different one.
      previewHtmlRef.current = null;
      // The permission set belongs to a build of THIS session's files; carrying
      // the previous session's over would enforce the wrong plugin's scopes.
      setPreviewPermissions(null);
      try {
        const result = await api.getPluginBuilderSession(sessionId);
        announceHealIfRepointed(result.buildPlanHealed);
        const sess = result.session as PluginBuilderSession;
        setSession(sess);
        setMessages(
          sess.conversationHistory.map((m) => ({
            role: m.role,
            content: m.content,
            timestamp: m.timestamp,
          })),
        );

        const settings = sess.currentSettings as Record<string, unknown>;
        if (settings?.currentFiles) {
          const loadedFiles = settings.currentFiles as VirtualFileSystem;
          setFiles(loadedFiles);
          filesRef.current = loadedFiles;
        }
        if (settings?.guidedFlow) {
          setGuidedFlow(settings.guidedFlow as GuidedFlow);
        }
        if (settings?.buildPlan) {
          setBuildPlan(settings.buildPlan as BuildPlan);
        }

        // Extract SDK update info from session response
        const sessionWithSdkUpdate = result.session as PluginBuilderSession & {
          sdkUpdate?: { available: boolean; from: string | null; to: string; hasBreaking: boolean };
        };
        setSdkUpdate(sessionWithSdkUpdate.sdkUpdate ?? null);

        // Record the server's version as our freshness baseline.
        const serverVersion = sess.version ?? UNKNOWN_BASE_VERSION;
        serverVersionRef.current = serverVersion;

        // Reconcile the local IndexedDB undo history against the server.
        //
        // The local snapshot only wins when it was last synced to the SAME
        // server version. Otherwise the server is ahead — edited on another
        // device — and its files win. This is what makes a hard refresh
        // actually reflect a cross-device change: IndexedDB survives reload, so
        // without a version check a stale local snapshot silently masks the
        // newer server state (the original bug).
        const savedHistory = await loadUndoHistory(sessionId);
        const serverFiles = filesRef.current;
        const hasServerFiles = !!serverFiles && Object.keys(serverFiles).length > 0;

        if (
          savedHistory &&
          savedHistory.states.length > 0 &&
          savedHistory.baseVersion === serverVersion
        ) {
          // Same base — local undo history is current; restore it so undo/redo
          // survives the refresh.
          const idx = Math.min(Math.max(savedHistory.index, 0), savedHistory.states.length - 1);
          restoreHistory(savedHistory.states as VirtualFileSystem[], idx);
          const restored = savedHistory.states[idx] as VirtualFileSystem;
          setFiles(restored);
          filesRef.current = restored;
        } else if (hasServerFiles) {
          // Server is ahead, or there's no/stale local history → server files
          // win. Reset undo history to a single server snapshot and overwrite
          // any stale persisted history so it can't resurface on the next load.
          restoreHistory([serverFiles], 0);
          saveHistory(sessionId, [serverFiles], 0, serverVersion);
        }

        // Build preview with the finalized files
        const finalFiles = filesRef.current;
        if (finalFiles && Object.keys(finalFiles).length > 0) {
          buildPreviewRef.current(finalFiles);
        }
      } catch (err) {
        const message = (err as Error).message;
        setError(message);
        onErrorRef.current?.(err as Error, apiErrorCode(err));
      } finally {
        setIsLoading(false);
      }
    },
    [loadUndoHistory, restoreHistory],
  );

  // Pull a newer session state that another device produced while this tab was
  // open. The local cache reconcile in loadSession only runs on mount, so a tab
  // that's been sitting open never learns about a remote edit — this closes
  // that gap on tab refocus and on a save conflict (409). Server always wins:
  // we never push this tab's stale files over the newer server state.
  const refreshIfStale = useCallback(
    async (reason: string) => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      // Don't interrupt an in-flight stream/guided generation — the post-stream
      // re-fetch reconciles, and pulling mid-stream could drop streaming files.
      if (isSendingRef.current || guidedStreamRequestIdRef.current) return;
      try {
        const result = await api.getPluginBuilderSession(sid);
        announceHealIfRepointed(result.buildPlanHealed);
        const sess = result.session as PluginBuilderSession;
        const serverVersion = sess.version ?? UNKNOWN_BASE_VERSION;
        // Only act when the server is genuinely ahead of what this tab holds.
        if (serverVersion <= serverVersionRef.current) return;

        serverVersionRef.current = serverVersion;
        setSession(sess);
        setMessages(
          sess.conversationHistory.map((m) => ({
            role: m.role,
            content: m.content,
            timestamp: m.timestamp,
          })),
        );
        const settings = sess.currentSettings as Record<string, unknown>;
        if (settings?.guidedFlow) setGuidedFlow(settings.guidedFlow as GuidedFlow);
        if (settings?.buildPlan) setBuildPlan(settings.buildPlan as BuildPlan);

        const serverFiles = (settings?.currentFiles as VirtualFileSystem) ?? null;
        // Only rebuild the preview when the server's files genuinely differ from
        // what's mounted. A version bump from a settings/name/description write
        // (common, and on this very tab) leaves the code unchanged — rebuilding
        // for it needlessly reloads the iframe and destroys any in-progress
        // report. When unchanged, we still adopt the version/metadata above but
        // leave the live preview untouched.
        const filesChanged =
          serverFiles !== null &&
          Object.keys(serverFiles).length > 0 &&
          !vfsEqual(serverFiles, filesRef.current);
        if (filesChanged) {
          setFiles(serverFiles);
          filesRef.current = serverFiles;
          // Server wins: reset undo history to the server snapshot and
          // overwrite any stale persisted history.
          restoreHistory([serverFiles], 0);
          saveHistory(sid, [serverFiles], 0, serverVersion);
          // Silent rebuild: a background sync shouldn't flash the "Building…"
          // spinner. buildPreview also captures/restores live plugin state
          // across the reload via onBeforePreviewReload.
          buildPreviewRef.current(serverFiles, { silent: true });
          // Only warn about a "synced from another device" content swap when it
          // actually replaced the code the user is looking at.
          onSyncFromRemoteRef.current?.(reason);
        } else {
          // Files are identical but the server version advanced (a settings /
          // name / description write). Keep the live preview and undo stack —
          // but re-tag the persisted history to the new version. Otherwise its
          // baseVersion lags serverVersionRef, and on the next page load
          // loadSession's `baseVersion === serverVersion` reconcile discards a
          // still-valid undo stack as "stale".
          const { states, index } = getHistoryRef.current();
          if (states.length > 0) {
            saveHistory(sid, states, index, serverVersion);
          }
        }
      } catch {
        // Best-effort background refresh — ignore transient failures.
      }
    },
    [restoreHistory],
  );
  refreshIfStaleRef.current = refreshIfStale;

  // Refetch on tab refocus / visibility regain so an open tab catches a
  // cross-device edit without a manual reload.
  useEffect(() => {
    const onFocus = () => refreshIfStaleRef.current('focus');
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshIfStaleRef.current('focus');
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Stall watchdog: while a turn is in flight, poll the last-frame timestamp
  // and recover if the backend has gone silent past the threshold. This is the
  // only escape from a turn whose process died without emitting a terminal
  // frame (the OOM-mid-turn case) — otherwise the spinner hangs indefinitely.
  const isStreamActive = isSending || isGuidedLoading;
  useEffect(() => {
    if (!isStreamActive) return;
    // Baseline = turn start. Set here (not in each send/guided-start path) so
    // every entry point is covered in one place. Per-frame liveness comes from
    // the transport below.
    turnStartRef.current = Date.now();

    const interval = setInterval(() => {
      // True silence = time since the later of (turn start, last inbound frame
      // of ANY type). getLastMessageAt covers every frame — tokens, tool calls,
      // files/settings, and the server heartbeat that fires during long-but-alive
      // gaps — so this only trips when the backend process is actually gone.
      const lastActivity = Math.max(turnStartRef.current, streaming.getLastMessageAt());
      if (Date.now() - lastActivity < stallTimeoutMs) return;
      clearInterval(interval);

      const wasGuided = guidedStreamRequestIdRef.current !== null;

      // 1) Stand the page down FIRST, before isSending flips. Its auto-approve
      //    effect keys on the isSending true→false edge; without userStopped set
      //    it would fire a fresh turn 2s later and silently re-bill the dead one
      //    (the 7×-retry loop from the incident). Runs synchronously so the ref
      //    is set before React re-renders.
      onStallRef.current?.();

      // 2) Clear the in-flight guards synchronously so refreshIfStale (which
      //    bails while isSendingRef/guidedStreamRequestIdRef are set) can run.
      isSendingRef.current = false;
      guidedStreamRequestIdRef.current = null;

      // 3) Surface a retryable timeout and drop all streaming state.
      const timeoutMessage =
        'The builder stopped responding. Click Continue to resume from the last saved step.';
      if (wasGuided) {
        setIsGuidedLoading(false);
        setGuidedError(timeoutMessage);
      }
      setIsSending(false);
      setRequestId(null);
      setError(timeoutMessage);
      setTruncated(false);
      // A stalled stream is the canonical RETRYABLE failure — light the inline
      // Retry (review finding: the watchdog used to only toast, leaving chat
      // mode with no recovery affordance at all).
      setLastFailure({ code: 'STREAM_TIMEOUT', message: timeoutMessage, retryable: true });
      streamingTextRef.current = '';
      streamingResetRef.current();
      onErrorRef.current?.(new Error(timeoutMessage), 'STREAM_TIMEOUT');

      // 4) Reconcile against whatever the server actually persisted (its last
      //    checkpoint), replacing the abandoned in-flight view.
      refreshIfStaleRef.current('stream-stalled');
    }, stallCheckIntervalMs);

    return () => clearInterval(interval);
  }, [isStreamActive, stallTimeoutMs, stallCheckIntervalMs]);

  const sendMessage = useCallback(
    async (message: string, guidedPhase?: GuidedPhase, autoApprove?: boolean) => {
      const sid = sessionIdRef.current;
      if (!sid || isSending) return;

      setIsSending(true);
      setError(null);
      setTruncated(false); // clear any prior cut-off notice when a new turn starts
      setLastFailure(null);
      // Fresh turn: any stale error-terminal marker from a lost stream must
      // not swallow THIS turn's successful completion.
      errorTerminalStreamRef.current = false;
      lastSentRef.current = { message, guidedPhase, autoApprove };
      streamingTextRef.current = '';

      // Add user message to local state immediately
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: message, timestamp: new Date().toISOString() },
      ]);

      try {
        const result = await api.startPluginBuilderMessageStream(
          sid,
          message,
          verboseRef.current,
          useOpusRef.current,
          guidedPhase,
          autoApprove,
        );
        setRequestId(result.requestId);
      } catch (err) {
        const errMessage = (err as Error).message;
        setError(errMessage);
        setIsSending(false);
        onErrorRef.current?.(err as Error, apiErrorCode(err));
      }
    },
    [isSending],
  );

  const advanceStep = useCallback(async (): Promise<boolean> => {
    const sid = sessionIdRef.current;
    if (!sid) return false;
    try {
      const result = await api.advancePluginBuilderStep(sid);
      // Update local build plan state from the server response
      if (result.buildPlan) {
        setBuildPlan(result.buildPlan as BuildPlan);
      }
      return true;
    } catch (err) {
      // A 409 means the advance was rejected (the current step isn't built yet —
      // STEP_NOT_BUILT — or a concurrent write raced us). Reconcile to server
      // truth so the on-screen pointer can't drift past what was durably saved,
      // and report failure so the caller doesn't tell the AI to continue.
      if (err instanceof ApiError && err.status === 409) {
        refreshIfStaleRef.current('conflict');
        setError(
          err.code === 'STEP_NOT_BUILT'
            ? 'This step has not been built yet. Press Continue to build it first.'
            : err.message,
        );
        return false;
      }
      setError((err as Error).message);
      return false;
    }
  }, []);

  const startGuidedFlow = useCallback(async (idea: string) => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsGuidedLoading(true);
    setGuidedError(null);
    streamingTextRef.current = '';

    // Add user message immediately so conversation view appears during loading
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: `I want to build an Arche: ${idea}`,
        timestamp: new Date().toISOString(),
      },
    ]);

    try {
      const result = await api.startPluginGuidedFlow(
        sid,
        idea,
        verboseRef.current,
        undefined,
        useOpusRef.current,
      );
      guidedStreamRequestIdRef.current = result.requestId;
      setRequestId(result.requestId); // Triggers WebSocket subscription
      setBuilderMode('guided');
    } catch (err) {
      const message = (err as Error).message;
      setGuidedError(message);
      setError(message);
      setIsGuidedLoading(false);
      onErrorRef.current?.(err as Error, apiErrorCode(err));
    }
  }, []);

  const submitAnswers = useCallback(async (answers: Record<number, string>, comment?: string) => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsGuidedLoading(true);
    setGuidedError(null);
    streamingTextRef.current = '';

    // Add user message immediately so conversation view shows during loading
    const qaList = guidedFlowRef.current?.clarifyingQA;
    const answersSummary = Object.entries(answers)
      .map(([i, a]) => {
        const question = qaList?.[Number(i)]?.question ?? `Q${Number(i) + 1}`;
        return `Q: ${question}\nA: ${a}`;
      })
      .join('\n\n');
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: `Here are my answers:\n${answersSummary}${comment ? `\n\nAdditional context: ${comment}` : ''}`,
        timestamp: new Date().toISOString(),
      },
    ]);

    try {
      const result = await api.submitGuidedAnswers(
        sid,
        answers,
        verboseRef.current,
        undefined,
        comment,
        useOpusRef.current,
      );
      guidedStreamRequestIdRef.current = result.requestId;
      setRequestId(result.requestId); // Triggers WebSocket subscription
    } catch (err) {
      const message = (err as Error).message;
      setGuidedError(message);
      setIsGuidedLoading(false);
      onErrorRef.current?.(err as Error, apiErrorCode(err));
    }
  }, []);

  const approvePlan = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsGuidedLoading(true);
    setGuidedError(null);
    streamingTextRef.current = '';

    // Add user message immediately
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: 'I approve this build plan.',
        timestamp: new Date().toISOString(),
      },
    ]);

    try {
      const result = await api.submitGuidedPlanDecision(
        sid,
        'approve',
        verboseRef.current,
        undefined,
        undefined,
        useOpusRef.current,
      );
      guidedStreamRequestIdRef.current = result.requestId;
      setRequestId(result.requestId); // Triggers WebSocket subscription
    } catch (err) {
      const message = (err as Error).message;
      setGuidedError(message);
      setIsGuidedLoading(false);
      onErrorRef.current?.(err as Error, apiErrorCode(err));
    }
  }, []);

  const requestPlanChanges = useCallback(async (feedback: string) => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsGuidedLoading(true);
    setGuidedError(null);
    streamingTextRef.current = '';

    // Add user message immediately
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: `I'd like changes to the plan: ${feedback}`,
        timestamp: new Date().toISOString(),
      },
    ]);

    try {
      const result = await api.submitGuidedPlanDecision(
        sid,
        'request-changes',
        verboseRef.current,
        feedback,
        undefined,
        useOpusRef.current,
      );
      guidedStreamRequestIdRef.current = result.requestId;
      setRequestId(result.requestId); // Triggers WebSocket subscription
    } catch (err) {
      const message = (err as Error).message;
      setGuidedError(message);
      setIsGuidedLoading(false);
      onErrorRef.current?.(err as Error, apiErrorCode(err));
    }
  }, []);

  const approveAssumptions = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    // Add user message immediately so conversation has a visible record of the approval
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: "I approve these assumptions. Let's start building.",
        timestamp: new Date().toISOString(),
      },
    ]);

    const controller = new AbortController();
    guidedAbortRef.current = controller;
    setIsGuidedLoading(true);
    setGuidedError(null);
    try {
      const result = await api.submitGuidedAssumptionDecision(
        sid,
        'approve',
        undefined,
        undefined,
        controller.signal,
        useOpusRef.current,
      );
      // Approve is local-only (no Claude call), apply immediately
      setGuidedFlow(result.guidedFlow as GuidedFlow);
      if (result.buildPlan) {
        setBuildPlan(result.buildPlan as BuildPlan);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      const message = (err as Error).message;
      setGuidedError(message);
      onErrorRef.current?.(err as Error, apiErrorCode(err));
    } finally {
      guidedAbortRef.current = null;
      setIsGuidedLoading(false);
    }
  }, []);

  const suggestAssumptionChanges = useCallback(async (comments: Record<string, string>) => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsGuidedLoading(true);
    setGuidedError(null);
    streamingTextRef.current = '';

    // Add user message immediately — include the assumption text for context
    const assumptions = guidedFlowRef.current?.assumptions ?? [];
    const feedbackSummary = Object.entries(comments)
      .map(([id, fb]) => {
        const assumption = assumptions.find((a) => a.id === id);
        const label = assumption?.assumption ?? id;
        return `Assumption: ${label}\nFeedback: ${fb}`;
      })
      .join('\n\n');
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: `I have feedback on some assumptions:\n\n${feedbackSummary}`,
        timestamp: new Date().toISOString(),
      },
    ]);

    try {
      const result = await api.submitGuidedAssumptionDecision(
        sid,
        'suggest-changes',
        verboseRef.current,
        comments,
        undefined,
        useOpusRef.current,
      );
      if (result.requestId) {
        guidedStreamRequestIdRef.current = result.requestId;
        setRequestId(result.requestId); // Triggers WebSocket subscription
      }
    } catch (err) {
      const message = (err as Error).message;
      setGuidedError(message);
      setIsGuidedLoading(false);
      onErrorRef.current?.(err as Error, apiErrorCode(err));
    }
  }, []);

  const applyPendingGuidedResult = useCallback(() => {
    const pending = pendingGuidedResultRef.current;
    if (!pending) return;

    if (pending.guidedFlow) {
      setGuidedFlow(pending.guidedFlow);
    }
    if (pending.buildPlan !== undefined) {
      setBuildPlan(pending.buildPlan);
    }

    pendingGuidedResultRef.current = null;
    setHasPendingGuidedResult(false);
  }, []);

  const cancelGuidedRequest = useCallback(() => {
    if (guidedAbortRef.current) {
      guidedAbortRef.current.abort();
      guidedAbortRef.current = null;
    }

    // Clear guided stream state
    guidedStreamRequestIdRef.current = null;
    streamingTextRef.current = '';
    setRequestId(null);
    setIsGuidedLoading(false);
    setGuidedError(null);

    // Re-fetch session to pick up server-side state changes (e.g., saved answers)
    const sid = sessionIdRef.current;
    if (sid) {
      api
        .getPluginBuilderSession(sid)
        .then((result) => {
          const sess = result.session as PluginBuilderSession;
          const settings = sess.currentSettings as Record<string, unknown>;
          if (settings?.guidedFlow) {
            setGuidedFlow(settings.guidedFlow as GuidedFlow);
          }
          if (settings?.buildPlan) {
            setBuildPlan(settings.buildPlan as BuildPlan);
          }
        })
        .catch(() => {
          // Best effort — don't fail on re-fetch
        });
    }
  }, []);

  const rewindToPhase = useCallback(
    async (
      targetPhase: GuidedPhase,
      options?: { confirmProgressLoss?: boolean; expectedVersion?: number },
    ) => {
      const sid = sessionIdRef.current;
      if (!sid) return;

      try {
        const result = await api.rewindGuidedPhase(sid, targetPhase, options);

        // Only once the server has rewound: cancel any in-flight guided
        // request and drop a pending guided result. A refused rewind (the
        // "this loses progress" 409) must be a true no-op — the user may
        // answer "Keep my plan".
        if (guidedAbortRef.current) {
          guidedAbortRef.current.abort();
          guidedAbortRef.current = null;
        }
        setIsGuidedLoading(false);
        setIsSending(false);
        pendingGuidedResultRef.current = null;
        setHasPendingGuidedResult(false);

        setGuidedFlow(result.guidedFlow as GuidedFlow);
        if (result.buildPlan) {
          setBuildPlan(result.buildPlan as BuildPlan);
        } else {
          setBuildPlan(null);
        }
        setBuilderMode('guided');
      } catch (err) {
        // The server's "this loses progress, confirm first" refusal is the
        // page's to handle (it opens the dialog) — not a failure to report.
        if (apiErrorCode(err) === 'REWIND_LOSES_PROGRESS') throw err;
        onErrorRef.current?.(err as Error, apiErrorCode(err));
      }
    },
    [],
  );

  const saveSession = useCallback(async (name: string, description?: string) => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    try {
      await api.savePluginBuilderSession(sid, { name, description });
      setSession((prev) => (prev ? { ...prev, currentName: name } : null));
    } catch (err) {
      setError((err as Error).message);
      onErrorRef.current?.(err as Error, apiErrorCode(err));
    }
  }, []);

  const resetContext = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsResetting(true);
    try {
      await api.resetPluginBuilderContext(sid);
      setMessages([]);
    } catch (err) {
      setError((err as Error).message);
      onErrorRef.current?.(err as Error, apiErrorCode(err));
    } finally {
      setIsResetting(false);
    }
  }, []);

  const stopGeneration = useCallback(() => {
    if (!isSending) return;

    // Capture partial text
    const partialText = streamingTextRef.current.trim();
    if (partialText) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: partialText + '\n\n_(Generation stopped)_',
          timestamp: new Date().toISOString(),
        },
      ]);
    }

    // Reset streaming state
    streaming.reset();
    setRequestId(null);
    setIsSending(false);
    streamingTextRef.current = '';
  }, [isSending, streaming]);

  const uploadPreviewScreenshot = useCallback((dataUrl: string) => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    api.uploadPluginPreviewScreenshot(sid, dataUrl).catch((err) => {
      console.warn('[usePluginBuilderSession] Failed to upload preview screenshot:', err);
    });
  }, []);

  // ─── Preview ────────────────────────────────────────────────────────────────

  // Monotonic counter to discard stale build results.
  // When undo triggers a new build while the auto-build is still in-flight,
  // the auto-build's response must not overwrite the undo's newer result.
  const buildSeqRef = useRef(0);

  const buildPreview = useCallback(
    async (
      filesToBuild?: VirtualFileSystem,
      opts?: { silent?: boolean; force?: boolean },
    ): Promise<string | null> => {
      const sid = sessionIdRef.current;
      if (!sid) return null;

      // Silent builds (background cross-device sync) skip the "Building…"
      // spinner so a passive refresh doesn't blink the preview. The spinner
      // unmounts the iframe, which is itself a reload — exactly what a no-op
      // sync must avoid.
      const silent = opts?.silent ?? false;

      const seq = ++buildSeqRef.current;
      if (!silent) {
        setIsBuildingPreview(true);
        setBuildError(null);
      }

      try {
        let result: Awaited<ReturnType<typeof api.buildPluginPreview>>;
        const issuedAtMono = performance.now();
        try {
          result = await api.buildPluginPreview(
            sid,
            filesToBuild as Record<string, unknown> | undefined,
            getPreviewModeRef.current?.(),
          );
        } catch (err) {
          // Only the build REQUEST's own failure is reported, and only when this
          // is still the latest build the user can see fail.
          if (buildSeqRef.current === seq && !silent) {
            reportPreviewBuildRefusal(sid, err, issuedAtMono);
          }
          throw err;
        }
        // Discard result if a newer build was started while this one was in-flight
        if (buildSeqRef.current !== seq) return null;
        if (result.success) {
          // Idempotency guard: re-assigning an identical srcDoc still reloads
          // the iframe and wipes the running plugin's in-memory state. If the
          // freshly-built HTML matches what's mounted, there's nothing to do.
          if (result.html === previewHtmlRef.current) {
            // Identical HTML, but the permissions may still have moved: a
            // manifest-only edit changes nothing in the bundle (the manifest
            // isn't bundled), so this is the ONLY branch that sees such a
            // change. Push it — the bridge host survives an identical-HTML
            // build and would otherwise keep enforcing the stale set.
            if (result.permissions) setPreviewPermissions(result.permissions);
            // A now-identical build supersedes any stale pending stash.
            pendingPreviewHtmlRef.current = null;
            setHasPendingPreview(false);
            return result.html;
          }
          // Interaction-aware deferral (Phase 5): while the user is actively
          // using the rendered plugin, a non-forced rebuild must not destroy
          // its state. Stash the fresh HTML; the pane's Refresh pill applies
          // it on the user's terms. Explicit builds (Build button, undo/redo,
          // SDK upgrade) pass force and always swap. When nothing is mounted
          // yet there is nothing to lose — always swap.
          if (
            !opts?.force &&
            previewHtmlRef.current !== null &&
            shouldDeferPreviewSwapRef.current?.()
          ) {
            pendingPreviewHtmlRef.current = {
              html: result.html,
              permissions: result.permissions ?? null,
            };
            setHasPendingPreview(true);
            return result.html;
          }
          // We're about to swap content → the iframe reloads and live plugin
          // state is lost. Give the host a chance to snapshot it first (no-op
          // unless this is a genuine reload of an already-rendered preview).
          if (previewHtmlRef.current !== null) {
            await onBeforePreviewReloadRef.current?.();
            if (buildSeqRef.current !== seq) return null;
          }
          previewHtmlRef.current = result.html;
          setPreviewHtml(result.html);
          if (result.permissions) setPreviewPermissions(result.permissions);
          pendingPreviewHtmlRef.current = null;
          setHasPendingPreview(false);
          return result.html;
        }
        return null;
      } catch (err) {
        if (buildSeqRef.current !== seq) return null;
        if (!silent) setBuildError((err as Error).message);
        return null;
      } finally {
        // Only clear the building flag if this is still the latest build
        if (buildSeqRef.current === seq && !silent) {
          setIsBuildingPreview(false);
        }
      }
    },
    [],
  );

  /** Swap the deferred (pending) preview in — the Refresh pill's action. */
  const applyPendingPreview = useCallback(async () => {
    const pending = pendingPreviewHtmlRef.current;
    if (pending === null) return;
    if (pending.html !== previewHtmlRef.current) {
      await onBeforePreviewReloadRef.current?.();
      previewHtmlRef.current = pending.html;
      setPreviewHtml(pending.html);
      // The stashed set belongs to the stashed bundle — they swap as one unit.
      if (pending.permissions) setPreviewPermissions(pending.permissions);
    }
    pendingPreviewHtmlRef.current = null;
    setHasPendingPreview(false);
  }, []);

  // Keep the ref up to date so []‑dep callbacks (onComplete, onError) can call buildPreview
  buildPreviewRef.current = buildPreview;
  streamingResetRef.current = streaming.reset;

  // ─── Undo/Redo Actions ──────────────────────────────────────────────────────

  const undo = useCallback(() => {
    const previousState = undoHistory();
    if (previousState !== null) {
      setFiles(previousState);
      filesRef.current = previousState;
      scheduleSaveRef.current();
      scheduleBackendSave(previousState);
      // Direct build for immediate feedback; buildSeqRef discards stale results
      buildPreview(previousState, { force: true });
      addBreadcrumb('undo-redo', 'Undo applied', {
        fileCount: Object.keys(previousState).length,
      });
    }
  }, [undoHistory, scheduleBackendSave, buildPreview]);

  const redo = useCallback(() => {
    const nextState = redoHistory();
    if (nextState !== null) {
      setFiles(nextState);
      filesRef.current = nextState;
      scheduleSaveRef.current();
      scheduleBackendSave(nextState);
      // Direct build for immediate feedback; buildSeqRef discards stale results
      buildPreview(nextState, { force: true });
      addBreadcrumb('undo-redo', 'Redo applied', {
        fileCount: Object.keys(nextState).length,
      });
    }
  }, [redoHistory, scheduleBackendSave, buildPreview]);

  const upgradeSdk = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsUpgradingSdk(true);
    try {
      const result = await api.upgradePluginBuilderSdk(sid);
      const sess = result.session as PluginBuilderSession;
      setSession(sess);
      if (typeof sess.version === 'number') serverVersionRef.current = sess.version;

      const settings = sess.currentSettings as Record<string, unknown>;
      if (settings?.currentFiles) {
        const loadedFiles = settings.currentFiles as VirtualFileSystem;
        setFiles(loadedFiles);
        filesRef.current = loadedFiles;
      }

      setSdkUpdate(null);

      // If breaking changes, trigger AI migration
      if (result.triggerAiMigration) {
        await sendMessage(
          'Please review my plugin code against the updated SDK and fix any incompatibilities. Show me what you changed.',
        );
      }

      // Rebuild preview
      if (filesRef.current && Object.keys(filesRef.current).length > 0) {
        buildPreviewRef.current(filesRef.current, { force: true });
      }
    } catch (err) {
      setError((err as Error).message);
      onErrorRef.current?.(err as Error, apiErrorCode(err));
    } finally {
      setIsUpgradingSdk(false);
    }
  }, [sendMessage]);

  /**
   * Re-send the last user message with identical phase/autoApprove — the
   * one-click Retry for typed transient failures (PROVIDER_TIMEOUT /
   * PROVIDER_OVERLOADED). The failed exchange is already persisted server-side
   * with a failure note; the server's turn-identity dedupe makes the re-send
   * safe against double-append.
   */
  const retryLastMessage = useCallback(() => {
    const last = lastSentRef.current;
    if (!last) return;
    void sendMessage(last.message, last.guidedPhase, last.autoApprove);
  }, [sendMessage]);

  return {
    session,
    isLoading,
    error,
    files,
    messages,
    streamingText: currentStreamingText,
    currentActivity,
    isSending,
    truncated,
    lastFailure,
    retryLastMessage,
    hasPendingPreview,
    applyPendingPreview,
    guidedFlow,
    buildPlan,
    builderMode,
    isGuidedLoading,
    guidedError,
    canUndo,
    canRedo,
    undoCount,
    redoCount,
    undo,
    redo,
    loadSession,
    sendMessage,
    startGuidedFlow,
    setBuilderMode,
    saveSession,
    resetContext,
    stopGeneration,
    uploadPreviewScreenshot,
    advanceStep,
    submitAnswers,
    approvePlan,
    requestPlanChanges,
    approveAssumptions,
    suggestAssumptionChanges,
    applyPendingGuidedResult,
    hasPendingGuidedResult,
    cancelGuidedRequest,
    rewindToPhase,
    buildPreview,
    previewHtml,
    previewPermissions,
    isBuildingPreview,
    buildError,
    isResetting,
    sdkUpdate,
    isUpgradingSdk,
    upgradeSdk,
    setSessionName: useCallback(
      (name: string) => setSession((prev) => (prev ? { ...prev, currentName: name } : null)),
      [],
    ),
    setSessionDescription: useCallback(
      (description: string) =>
        setSession((prev) => (prev ? { ...prev, currentDescription: description } : null)),
      [],
    ),
    setSessionExpandedDescription: useCallback(
      (expandedDescription: string) =>
        setSession((prev) =>
          prev ? { ...prev, currentExpandedDescription: expandedDescription } : null,
        ),
      [],
    ),
    updateSessionSettings: useCallback(
      (settings: Record<string, unknown>) =>
        setSession((prev) =>
          prev
            ? {
                ...prev,
                currentSettings: {
                  ...((prev.currentSettings as Record<string, unknown>) || {}),
                  ...settings,
                },
              }
            : null,
        ),
      [],
    ),
    setBuildPlan,
    noteServerVersion,
  };
}
